<template>
    <div>
        <rkm-area
            v-if="pacienteExists"
            title="Paciente"
            type="fieldset"
            no-margin-top
        >
            <rkm-row>
                <rkm-col
                    v-if="showAvatar"
                    align-self="center"
                    class="col-svg flex-grow-0"
                >
                    <rkm-icon class="icon" icon="user"/>
                </rkm-col>

                <rkm-col class="flex-grow-1">
                    <rkm-row>
                        <rkm-col>
                            <h4>
                                {{ data.form.paciente.cidadao.nome }}
                                <strong v-if="data.form.paciente.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                                    ({{ data.form.paciente.nomeSocial }})
                                </strong>

                                <span
                                    v-if="isResponsavelFamiliar"
                                    class="ml-1"
                                    title="Responsável familiar"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="crown" />
                                </span>

                                <span
                                    v-if="isTea"
                                    class="ml-1"
                                    title="Pessoa com Transtorno do Espectro Autista - TEA"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="infinity" />
                                </span>

                                <span
                                    v-if="isPcd"
                                    class="ml-1"
                                    title="Pessoa com Deficiência - PCD"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="wheelchair" />
                                </span>
                            </h4>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row v-if="data.form.acompanhamentosSaude && data.form.acompanhamentosSaude.length > 0">
                        <rkm-col>
                            <div class="health-flags">
                                <span class="health-title">
                                    Acompanhamentos:
                                </span>

                                <span
                                    v-for="(acompanhamento, index) in data.form.acompanhamentosSaude"
                                    :key="index"
                                    :class="`health-badge acompanhamento-${acompanhamento.value}`"
                                >
                                    {{ acompanhamento.text }}
                                </span>
                            </div>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="3">
                            <p>
                                <strong>Prontuário:</strong>
                                {{ data.form.paciente.prontuario }}
                            </p>
                            <p>
                                <strong>CPF:</strong>
                                {{ data.form.paciente.cidadao.cpf | formatCpf }}
                            </p>
                            <p>
                                <strong>CNS:</strong>
                                {{ data.form.paciente.cidadao.cns | formatCns }}
                            </p>
                            <p>
                                <strong>RG:</strong>
                                <template v-if="data.form.paciente.cidadao.carteiraIdentidade">
                                    {{ data.form.paciente.cidadao.carteiraIdentidade.numero | formatRg }}
                                </template>
                                <template v-else>
                                    -
                                </template>
                            </p>
                            <p>
                                <strong>Telefone:</strong>
                                <template v-if="data.model.paciente.telefoneNumero">
                                    {{ data.model.paciente.telefoneNumero | formatPhoneOrCellPhone }}
                                </template>
                                <template v-else>
                                    -
                                </template>
                                <rkm-button
                                    v-if="verificarPermissao('update-cadastro-paciente')"
                                    @click="$refs.ModalTelefone.open(data.model.paciente)"
                                    class="ml-1"
                                    color="primary"
                                    title="Alterar telefone"
                                    v-rkm-tooltip.hover
                                    small
                                >
                                    <rkm-icon icon="pen" />
                                </rkm-button>
                            </p>
                        </rkm-col>

                        <rkm-col cols="3">
                            <p>
                                <strong>Data de nascimento:</strong>
                                {{ data.form.paciente.cidadao.dataNascimento | formatDate }}
                            </p>
                            <p>
                                <strong>Idade:</strong>
                                <template v-if="data.form.paciente.cidadao.idade">
                                    {{ data.form.paciente.cidadao.idade.anos }} anos,
                                    {{ data.form.paciente.cidadao.idade.meses }} meses e
                                    {{ data.form.paciente.cidadao.idade.dias }} dias
                                </template>
                                <template v-else>
                                    -
                                </template>
                            </p>
                            <p>
                                <strong>Sexo:</strong>
                                <template v-if="data.form.paciente.cidadao.sexo">
                                    {{ data.form.paciente.cidadao.sexo.descricao }}
                                </template>
                                <template v-else>
                                    -
                                </template>
                            </p>
                            <p>
                                <strong>Nome da mãe:</strong>
                                <template v-if="data.form.paciente.cidadao.nomeMae">
                                    {{ data.form.paciente.cidadao.nomeMae }}
                                </template>
                                <template v-else>
                                    <i>Desconhecido</i>
                                </template>
                            </p>
                        </rkm-col>

                        <rkm-col cols="3">
                            <p>
                                <strong>Classificação:</strong>
                                {{ classificacaoDescricao }}
                            </p>
                            <p>
                                <strong>Unidade de referência:</strong>
                                <template v-if="data.form.paciente.unidade && data.form.paciente.unidade.nomeFantasia">
                                    {{ data.form.paciente.unidade.nomeFantasia }}
                                </template>
                                <template v-else>
                                    <i>Não informado no cadastro do paciente</i>
                                </template>
                            </p>
                            <p>
                                <strong>Área e Microárea:</strong>
                                {{ areaEMicroarea }}
                            </p>
                            <p>
                                <strong>INE:</strong>
                                <template v-if="data.form.paciente.responsavel && data.form.paciente.responsavel.equipe && data.form.paciente.responsavel.equipe.area">
                                    {{ data.form.paciente.responsavel.equipe.area.numeroIne }}
                                </template>
                                <template v-else>
                                    Não Informado
                                </template>
                            </p>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col>
                            <p>
                                <strong>Endereço:</strong>
                                <template v-if="data.form.paciente.cidadao.domicilio && data.form.paciente.cidadao.domicilio.endereco">
                                    {{ data.form.paciente.cidadao.domicilio.endereco.logradouro.nome }},
                                    {{ data.form.paciente.cidadao.domicilio.endereco.numero }},
                                    {{ data.form.paciente.cidadao.domicilio.endereco.logradouro.bairro.nome }},
                                    {{ data.form.paciente.cidadao.domicilio.endereco.logradouro.municipio.nome }} -
                                    {{ data.form.paciente.cidadao.domicilio.endereco.logradouro.municipio.estado.nome }},
                                    CEP: {{ data.form.paciente.cidadao.domicilio.endereco.logradouro.cep | formatCep }}
                                </template>
                                <template v-else>
                                    Não informado
                                </template>
                            </p>
                        </rkm-col>
                    </rkm-row>
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <ModalTelefone ref="ModalTelefone" />
    </div>
</template>

<script>

// Components.
import ModalTelefone from './telefone/Modal'

// Mixins.
import { permissao } from '@/mixins'

// Enums.
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

// Filters.
import formats from '@/filters/formats'

export default {
    components: { ModalTelefone },
    computed: {
        pacienteExists() {
            return this.data.form && this.data.form.paciente
        },
        isResponsavelFamiliar() {
            return !!(this.data.form.paciente.cadastroIndividual && this.data.form.paciente.cadastroIndividual.responsavel)
        },
        deficienciaIds() {
            const informacaoSocial = this.data.form.paciente.informacaoSocial

            return informacaoSocial && informacaoSocial.deficiencia
                ? informacaoSocial.deficiencia.map(deficiencia => deficiencia.id)
                : []
        },
        isTea() {
            return this.deficienciaIds.includes(this.enums.deficienciaBase.TEA)
                || this.data.form.paciente.cidadao.deficienciaId === this.enums.deficiencia.AUTISMO
        },
        isPcd() {
            const cidadaoDeficienciaId = this.data.form.paciente.cidadao.deficienciaId

            return this.deficienciaIds.some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!cidadaoDeficienciaId && cidadaoDeficienciaId !== this.enums.deficiencia.AUTISMO)
        },
        classificacaoDescricao() {
            const paciente = this.data.form.paciente

            if (paciente.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO) {
                return 'Temporário'
            }

            if (paciente.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {
                return 'Outro Município'
            }

            return 'Munícipe'
        },
        areaEMicroarea() {
            const equipe = this.data.form.paciente.responsavel && this.data.form.paciente.responsavel.equipe

            if (!equipe) {
                return 'Não Informada'
            }

            const area = equipe.area ? equipe.area.descricao : 'Não Informada'
            const microarea = equipe.microarea ? equipe.microarea.descricao : 'Não Informada'

            return `${area} / ${microarea}`
        },
    },
    data: () => ({
        enums: { preferenciaNome, classificacao, subclassificacao, deficienciaBase, deficiencia },
    }),
    filters: {
        ...formats,
    },
    mixins: [ permissao ],
    props: {
        data: { type: Object, required: true },
        showAvatar: { type: Boolean, default: false }
    },
}
</script>

<style scoped>
p {
    margin: 0px;
}
.col-svg {
    text-align: center !important;
}
.icon {
    width: auto !important;
    height: 96px !important;
    color: #007bff !important;
}

.health-flags {
    margin-top: 9px;
    padding-top: 2px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    border-top: 1px solid #e5e7eb;
}

.health-title {
    font-size: 12px;
    color: #6b7280;
    font-weight: 600;
}

.health-badge {
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
}

.acompanhamento-idoso {
    color: #155724;
}

.acompanhamento-gestante {
    color: #ffb6c1;
}

.acompanhamento-hipertenso,
.acompanhamento-diabetico {
    color: #856404;
}

.acompanhamento-saude-mulher {
    color: #b34db2;
}

.acompanhamento-crescimento-desenvolvimento-infantil {
    color: #004085;
}
</style>
